import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { NOTIFICATION_CATEGORY, type NotificationCategory } from './constants';
import {
  notificationPageEnvelopeSchema,
  notificationSettingsWireSchema,
  unreadFlagSchema,
  type NotificationPage,
  type NotificationSettings,
} from './schemas';

/**
 * One page of notifications, newest-first (the server owns the ordering).
 *
 * `getEnvelope`, not `get`: this endpoint's `meta` — the cursor **and** the unread count — sits
 * beside `data` rather than inside it, and `get` peels `data` and discards the rest. See
 * `notificationPageEnvelopeSchema`.
 */
export async function fetchNotifications(query: {
  limit: number;
  cursor: string | null;
}): Promise<NotificationPage> {
  const params = new URLSearchParams({ limit: String(query.limit) });
  if (query.cursor) params.set('cursor', query.cursor);

  const body = await apiClient.getEnvelope<unknown>(endpoints.notifications.list, { params });
  return notificationPageEnvelopeSchema.parse(body ?? {});
}

/**
 * Mark the account's unread notifications read.
 *
 * **The endpoint takes no ids and marks every unread row.** That is the confirmed contract, not a
 * simplification — there is no per-id route (the `.../{id}/read` this app used to call was taken
 * from a proposal the backend never implemented, and answered 404).
 *
 * The story's "opening the screen marks the currently listed notifications as read" is still
 * satisfied, because the panel lists the feed. The § RETENTION concern that argued for per-row
 * marking has no client-side remedy left: with only a mark-all route, the server owns that policy.
 */
export async function markNotificationsRead(): Promise<void> {
  await apiClient.post(endpoints.notifications.markRead, {});
}

/**
 * The bell's unread count.
 *
 * Reads `meta.unreadCount` off the **list** endpoint with `limit=1` rather than `/unread`, which
 * answers only a boolean. One row is the smallest response that still carries the count, so this
 * stays cheap and never pages.
 *
 * Deliberately a different request from {@link fetchNotifications}: opening the panel marks
 * everything read, so driving the badge off the feed would zero the count merely by rendering.
 *
 * Falls back to `/unread` when the list sends no count, so the badge degrades to "1" rather than
 * vanishing — mirrors `ApiNotificationRepository.unreadCount` in Tracking Mobile.
 */
export async function fetchUnreadCount(): Promise<number> {
  const page = await fetchNotifications({ limit: 1, cursor: null });
  if (page.unreadCount > 0 || page.hasUnread) return page.unreadCount || 1;

  const body = await apiClient.getEnvelope<unknown>(endpoints.notifications.unread);
  const flag = unreadFlagSchema.parse(body ?? {});
  return flag.unreadCount ?? (flag.hasUnread ? 1 : 0);
}

export async function fetchNotificationSettings(): Promise<NotificationSettings> {
  const payload = await apiClient.get<unknown>(endpoints.notifications.settings);
  return notificationSettingsWireSchema.parse(payload ?? {});
}

/**
 * Persist one category toggle (US-113356: auto-save, no confirmation step).
 *
 * Sends only the category that changed, so two quick toggles cannot clobber each other by each
 * echoing back a whole stale settings object.
 */
export async function updateNotificationSetting(input: {
  category: NotificationCategory;
  enabled: boolean;
}): Promise<NotificationSettings> {
  // Three of the four category names are the wire's own; `requestsAndClaims` is not — the API
  // spells it `requestsClaims` (`GET /api/docs-json`, 2026-09-02). Sending the app's spelling
  // would have the server ignore the field and echo back the unchanged value, so the toggle would
  // spring back with no error to explain it.
  const wireKey =
    input.category === NOTIFICATION_CATEGORY.REQUESTS_AND_CLAIMS
      ? 'requestsClaims'
      : input.category;

  const payload = await apiClient.patch<unknown>(endpoints.notifications.settings, {
    [wireKey]: input.enabled,
  });
  return notificationSettingsWireSchema.parse(payload ?? {});
}
